/**
 * The translation runtime.
 *
 * Three properties this is built around:
 *
 *  - **The server decides the language.** A stored preference has to beat the
 *    browser's guess, and only the server can see the stored preference. The
 *    client detects a language only before there is a session -- on the sign-in
 *    screen -- and hands the result to the server on the very next request.
 *  - **One language is downloaded, not twenty-three.** Each locale is a dynamic
 *    `import()`, so Vite emits one chunk per language and a player fetches the
 *    one they read. English is bundled because it is also the fallback.
 *  - **Text is never built by concatenation.** Every sentence is one key with
 *    `{placeholders}`; word order differs between these languages far too much
 *    for "prefix" + value + "suffix" to survive translation.
 *
 * Language and currency are separate settings and never inferred from each
 * other. A player reading Turkish may hold a USD wallet.
 */

import { en, type MessageKey, type Messages } from './locales/en';

export type { MessageKey, Messages };

/** One offered language. Mirrors i18n.Language in server/internal/i18n. */
export interface LanguageInfo {
  tag: string;
  name: string;
  rtl: boolean;
}

/**
 * The offered set, in the order a picker should list it.
 *
 * This mirrors `Supported` in server/internal/i18n/i18n.go. The server's copy is
 * authoritative -- the handshake sends the list -- but this one exists so the
 * sign-in screen has a picker before there is a session, and so a build fails if
 * a language is added here without a translation file.
 */
export const LANGUAGES: readonly LanguageInfo[] = [
  { tag: 'en', name: 'English', rtl: false },
  { tag: 'es', name: 'Español', rtl: false },
  { tag: 'ru', name: 'Русский', rtl: false },
  { tag: 'zh', name: '中文', rtl: false },
  { tag: 'hi', name: 'हिन्दी', rtl: false },
  { tag: 'tr', name: 'Türkçe', rtl: false },
  { tag: 'ar', name: 'العربية', rtl: true },
  { tag: 'pt', name: 'Português', rtl: false },
  { tag: 'fr', name: 'Français', rtl: false },
  { tag: 'ur', name: 'اردو', rtl: true },
  { tag: 'id', name: 'Bahasa Indonesia', rtl: false },
  { tag: 'bn', name: 'বাংলা', rtl: false },
  { tag: 'de', name: 'Deutsch', rtl: false },
  { tag: 'ja', name: '日本語', rtl: false },
  { tag: 'pcm', name: 'Nigerian Pidgin', rtl: false },
  { tag: 'pnb', name: 'پنجابی', rtl: true },
  { tag: 'fa', name: 'فارسی', rtl: true },
  { tag: 'vi', name: 'Tiếng Việt', rtl: false },
  { tag: 'fil', name: 'Filipino', rtl: false },
  { tag: 'th', name: 'ไทย', rtl: false },
  { tag: 'ko', name: '한국어', rtl: false },
  { tag: 'it', name: 'Italiano', rtl: false },
  { tag: 'ms', name: 'Bahasa Melayu', rtl: false },
];

export const DEFAULT_LANGUAGE = 'en';

/**
 * How each language's table is fetched.
 *
 * The import specifiers are written out one by one rather than built from a
 * template string on purpose: a static specifier is what lets the bundler find
 * the file, split it into its own chunk, and fail the build if it is missing.
 */
const LOADERS: Record<string, () => Promise<Messages>> = {
  en: () => Promise.resolve(en),
  es: () => import('./locales/es').then((m) => m.default),
  ru: () => import('./locales/ru').then((m) => m.default),
  zh: () => import('./locales/zh').then((m) => m.default),
  hi: () => import('./locales/hi').then((m) => m.default),
  tr: () => import('./locales/tr').then((m) => m.default),
  ar: () => import('./locales/ar').then((m) => m.default),
  pt: () => import('./locales/pt').then((m) => m.default),
  fr: () => import('./locales/fr').then((m) => m.default),
  ur: () => import('./locales/ur').then((m) => m.default),
  id: () => import('./locales/id').then((m) => m.default),
  bn: () => import('./locales/bn').then((m) => m.default),
  de: () => import('./locales/de').then((m) => m.default),
  ja: () => import('./locales/ja').then((m) => m.default),
  pcm: () => import('./locales/pcm').then((m) => m.default),
  pnb: () => import('./locales/pnb').then((m) => m.default),
  fa: () => import('./locales/fa').then((m) => m.default),
  vi: () => import('./locales/vi').then((m) => m.default),
  fil: () => import('./locales/fil').then((m) => m.default),
  th: () => import('./locales/th').then((m) => m.default),
  ko: () => import('./locales/ko').then((m) => m.default),
  it: () => import('./locales/it').then((m) => m.default),
  ms: () => import('./locales/ms').then((m) => m.default),
};

const RTL_TAGS = new Set(LANGUAGES.filter((l) => l.rtl).map((l) => l.tag));

/**
 * Where a language chosen before signing in is remembered.
 *
 * The key still says `sandbag` after the game was renamed to Fighter, and that
 * is deliberate: renaming it would make every browser that already holds a
 * language forget it, since the new key reads nothing. A player would be dropped
 * back into English to save a string nobody sees. Keys added from now on use the
 * `fighter.` prefix.
 */
const STORAGE_KEY = 'sandbag.language';

let current = DEFAULT_LANGUAGE;
let table: Messages = en;

type Listener = () => void;
const listeners = new Set<Listener>();

export function currentLanguage(): string {
  return current;
}

export function isRTL(tag: string = current): boolean {
  return RTL_TAGS.has(tag);
}

export function isSupported(tag: string): boolean {
  return tag in LOADERS;
}

/**
 * Whether a key exists at all, for text built from a value the server chose.
 *
 * An error code is a string from the wire, not a literal in this codebase, so a
 * new code deployed on the server before the client knows it must fall through
 * to the server's own message rather than rendering "err.some_new_code".
 */
export function hasKey(key: string): key is MessageKey {
  return key in en;
}

export function languageInfo(tag: string): LanguageInfo {
  return LANGUAGES.find((l) => l.tag === tag) ?? LANGUAGES[0]!;
}

/**
 * Loads a language and makes it current.
 *
 * Applying it to the document -- the `lang` and `dir` attributes and every
 * element carrying a `data-i18n` attribute -- happens here, so a caller cannot
 * change the language and forget to redraw. Components that render text from
 * script register with `onLanguageChange`.
 */
export async function setLanguage(tag: string): Promise<void> {
  const resolved = isSupported(tag) ? tag : DEFAULT_LANGUAGE;
  const loader = LOADERS[resolved]!;

  try {
    table = await loader();
    current = resolved;
  } catch {
    // A chunk that fails to download must not take the interface with it:
    // English is already in the bundle.
    table = en;
    current = DEFAULT_LANGUAGE;
  }

  applyToDocument();
  for (const listener of listeners) listener();
}

/** Registers a callback to re-render script-generated text. */
export function onLanguageChange(listener: Listener): void {
  listeners.add(listener);
}

/**
 * Looks up a key and substitutes its placeholders.
 *
 * A key missing from the current table falls back to English rather than to the
 * key itself, because a sentence in the wrong language still tells the player
 * something and `panel.cashOut` does not.
 */
export function t(key: MessageKey, params?: Record<string, string | number>): string {
  const template = table[key] ?? en[key] ?? key;
  if (!params) return template;

  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : String(value);
  });
}

/**
 * The language to open in before there is a session.
 *
 * Order: a choice made on this device, then the browser's own list, then
 * English. `navigator.languages` is a list for a reason -- a browser set to
 * "de, tr" is telling us both, and the first one we actually offer wins.
 */
export function detectLanguage(): string {
  const stored = readStoredLanguage();
  if (stored) return stored;

  const requested = navigator.languages?.length
    ? navigator.languages
    : [navigator.language].filter(Boolean);

  for (const raw of requested) {
    const tag = raw.toLowerCase();
    if (isSupported(tag)) return tag;

    // "pt-BR" -> "pt". Regional variants share one translation.
    const primary = tag.split('-')[0]!;
    if (isSupported(primary)) return primary;
    // Tagalog is what several platforms still send for Filipino.
    if (primary === 'tl') return 'fil';
  }
  return DEFAULT_LANGUAGE;
}

export function readStoredLanguage(): string | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && isSupported(stored) ? stored : null;
  } catch {
    // Private browsing can make localStorage throw on access, which is not a
    // reason to fail to start.
    return null;
  }
}

export function storeLanguage(tag: string | null): void {
  try {
    if (tag === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, tag);
  } catch {
    /* see readStoredLanguage */
  }
}

/**
 * Rewrites every translatable attribute in the document.
 *
 * Markup declares what it needs with `data-i18n` (text), `data-i18n-title`,
 * `data-i18n-placeholder` and `data-i18n-aria`, which keeps the static chrome
 * out of the script and means a new label needs no code.
 */
export function applyToDocument(root: ParentNode = document): void {
  const html = document.documentElement;
  html.lang = current;
  html.dir = isRTL() ? 'rtl' : 'ltr';

  for (const el of root.querySelectorAll<HTMLElement>('[data-i18n]')) {
    const key = el.dataset['i18n'] as MessageKey | undefined;
    if (key) el.textContent = t(key);
  }
  for (const el of root.querySelectorAll<HTMLElement>('[data-i18n-title]')) {
    const key = el.dataset['i18nTitle'] as MessageKey | undefined;
    if (key) el.title = t(key);
  }
  for (const el of root.querySelectorAll<HTMLElement>('[data-i18n-placeholder]')) {
    const key = el.dataset['i18nPlaceholder'] as MessageKey | undefined;
    if (key && el instanceof HTMLInputElement) el.placeholder = t(key);
  }
  for (const el of root.querySelectorAll<HTMLElement>('[data-i18n-aria]')) {
    const key = el.dataset['i18nAria'] as MessageKey | undefined;
    if (key) el.setAttribute('aria-label', t(key));
  }
}
